import { inject, Injectable, Injector } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AssetFacade } from '@simOn/asset';
import { SidenavLayoutRegistry } from '@simOn/layout';
import { SpaceFacade } from '@simOn/space';
import { firstValueFrom, take } from 'rxjs';
import { NavigateToAssetActionDto } from '../model';

const PANEL_WIDTH = 'min(520px,100%)';

/** What is needed to show an asset: the `NavigateToAsset` card, or a freshly created asset. */
export type AssetNavigationTarget = Pick<NavigateToAssetActionDto, 'targetId' | 'apartmentId' | 'apartmentName'>;

/**
 * Handles the `NavigateToAsset` action. `navigate` opens the asset details in place: the view panel
 * slides into the right sidenav of the current page, the same way tasks and tickets open a linked asset.
 * `goToPosition` hands the position over to the 3D walk of the asset's space, like the assets dashboard.
 *
 * The asset view lives in `feature-asset`, which a feature library may not depend on statically; it is
 * loaded lazily like the task and ticket panels do.
 */
@Injectable({ providedIn: 'root' })
export class AgentAssetNavigationService {
  private readonly _router = inject(Router);
  private readonly _injector = inject(Injector);
  private readonly _layoutRegistry = inject(SidenavLayoutRegistry);
  private readonly _assetFacade = inject(AssetFacade);
  private readonly _spaceFacade = inject(SpaceFacade);
  // eslint-disable-next-line @nx/enforce-module-boundaries
  private _featureAssetChunk?: Promise<typeof import('feature-asset')>;

  async navigate(action: AssetNavigationTarget): Promise<void> {
    const selectedApartmentId = await firstValueFrom(this._spaceFacade.selectedApartmentId$.pipe(take(1)));
    const portal = this._layoutRegistry.createRightPortal(this._injector);

    // Asset of another space, or no sidenav layout on screen: land on the assets page of that space.
    if (!portal || selectedApartmentId !== action.apartmentId) {
      await this._router.navigate(['/space', action.apartmentId, 'dashboard', 'assets'], {
        queryParams: { assetId: action.targetId }
      });
      return;
    }

    // eslint-disable-next-line @nx/enforce-module-boundaries
    const { ViewModeAssetComponent } = await (this._featureAssetChunk ??= import('feature-asset'));
    this._assetFacade.setSelectedId(action.targetId);
    const ref = portal.open(ViewModeAssetComponent, { width: PANEL_WIDTH, hasBackdrop: false });
    if (action.apartmentName) ref.setInput('spaceName', action.apartmentName);
    outputToObservable(ref.instance.closePanel)
      .pipe(take(1))
      .subscribe(() => portal.close());
  }

  /**
   * Moves the 3D walk to the asset position and opens the asset there. The walk page consumes the armed
   * handoff on `goToAssetPosition`, also when it is already on screen (the query params change).
   */
  async goToPosition(action: NavigateToAssetActionDto): Promise<void> {
    if (!action.hasPosition || !action.transformation) {
      throw new Error(`[ai-agent] asset "${action.targetId}" has no 3D position`);
    }
    // eslint-disable-next-line @nx/enforce-module-boundaries
    const { AssetPlacementHandoffService } = await (this._featureAssetChunk ??= import('feature-asset'));
    this._injector.get(AssetPlacementHandoffService).armGoToPosition(action.transformation, action.targetId);
    await this._router.navigate(['/space', action.apartmentId, 'apartment'], {
      queryParams: { context: 'assets', goToAssetPosition: true }
    });
  }
}
